import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Users, KeyRound, Power, Activity } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import FileUploader from "@/components/FileUploader";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const ROLES = [
  { key: "institute_admin", label: "Institute Admin" },
  { key: "institute_staff", label: "Institute Staff" },
];

const empty = {
  username: "",
  email: "",
  name: "",
  role: "institute_staff",
  password: "",
  is_active: true,
  designation: "",
  department_id: "",
  photo_url: "",
  photo_public_id: "",
  resume_url: "",
  resume_public_id: "",
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}

const RolePill = ({ r }) => (
  <span
    className={`inline-block px-2 py-0.5 text-[10px] tracking-wide uppercase border rounded-sm ${
      r === "institute_admin"
        ? "bg-[#234333] text-white border-[#234333]"
        : "bg-[#6b655b]/10 text-[#6b655b] border-[#6b655b]/30"
    }`}
  >
    {r === "institute_admin" ? "Admin" : "Staff"}
  </span>
);

export default function InstituteStaff() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [activity, setActivity] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [resetPwFor, setResetPwFor] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const fetchAll = async () => {
    const [s, a, d, dp] = await Promise.all([
      api.get("/institute/staff"),
      api.get("/institute/login-activity"),
      api.get("/masters/staff_designation"),
      api.get("/institute/departments"),
    ]);
    setItems(s.data);
    setActivity(a.data);
    setDesignations(d.data);
    setDepartments(dp.data);
  };

  useEffect(() => {
    setLoading(true);
    fetchAll().finally(() => setLoading(false));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ ...empty, ...u, password: "" });
    setOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Detect teaching designation (excluding Principal)
  const designationMeta = designations.find((x) => x.name === form.designation);
  const isPrincipal = (form.designation || "").trim().toLowerCase() === "principal";
  const isTeaching =
    !!form.designation &&
    designationMeta?.staff_type === "teaching" &&
    !isPrincipal;
  const departmentDisabled = !form.designation || !isTeaching;
  // Clear department_id whenever it becomes disabled (e.g., designation switched to Principal/non-teaching)
  useEffect(() => {
    if (departmentDisabled && form.department_id) {
      setForm((f) => ({ ...f, department_id: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentDisabled]);

  const save = async () => {
    if (!editing && !form.password) {
      toast.error("Password is required for new staff");
      return;
    }
    if (!form.username && !form.email) {
      toast.error("Username or email is required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          name: form.name,
          email: form.email,
          role: form.role,
          is_active: form.is_active,
          designation: form.designation,
          department_id: isTeaching ? form.department_id : "",
          photo_url: form.photo_url,
          photo_public_id: form.photo_public_id,
          resume_url: form.resume_url,
          resume_public_id: form.resume_public_id,
        };
        const { data } = await api.put(`/institute/staff/${editing.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Staff updated");
      } else {
        const payload = {
          username: form.username || form.email,
          email: form.email,
          name: form.name,
          role: form.role,
          password: form.password,
          is_active: form.is_active,
          designation: form.designation,
          department_id: isTeaching ? form.department_id : "",
          photo_url: form.photo_url,
          photo_public_id: form.photo_public_id,
          resume_url: form.resume_url,
          resume_public_id: form.resume_public_id,
        };
        const { data } = await api.post(`/institute/staff`, payload);
        setItems((arr) => [data, ...arr]);
        toast.success("Staff added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const u = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/institute/staff/${u.id}`);
      setItems((arr) => arr.filter((x) => x.id !== u.id));
      toast.success("Staff removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleActive = async (u) => {
    try {
      const { data } = await api.put(`/institute/staff/${u.id}`, { is_active: !u.is_active });
      setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
      toast.success(data.is_active ? "Enabled" : "Disabled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    }
  };

  const doResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.post(`/institute/staff/${resetPwFor.id}/reset-password`, {
        new_password: newPassword,
      });
      toast.success("Password reset");
      setResetPwFor(null);
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Reset failed");
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-6xl mx-auto" data-testid="institute-staff-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">
            Access Control
          </div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Staff & Activity</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Manage your institute staff and review recent login activity.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="staff-add-button"
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm transition-colors"
        >
          <Plus size={14} strokeWidth={1.5} /> Add Staff
        </button>
      </div>

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : (
        <>
          <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto mb-10">
            <table className="w-full text-sm" data-testid="staff-table">
              <thead>
                <tr className="bg-[#f9f8f6] text-left">
                  {["", "Name", "Designation", "Department", "Login", "Role", "Active", "Resume", "Last login", ""].map(
                    (h, i) => (
                      <th
                        key={i}
                        className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr
                    key={u.id}
                    data-testid={`staff-row-${u.id}`}
                    className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60"
                  >
                    <td className="px-4 py-3">
                      {u.photo_url ? (
                        <img
                          src={u.photo_url}
                          alt={u.name}
                          className="w-9 h-9 rounded-full object-cover border border-[#e5e0d8]"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-[#f9f8f6] border border-[#e5e0d8] flex items-center justify-center font-serif text-[#234333] text-sm">
                          {(u.name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-[#1c1a17]">{u.name || "—"}</td>
                    <td className="px-4 py-4 text-[#6b655b]">{u.designation || "—"}</td>
                    <td className="px-4 py-4 text-[#6b655b]">
                      {u.department_id
                        ? departments.find((d) => d.id === u.department_id)?.name || "—"
                        : "—"}
                    </td>
                    <td className="px-4 py-4 text-[#6b655b]">
                      <div className="font-mono text-[12px]">{u.username}</div>
                      {u.email ? <div className="text-[11px]">{u.email}</div> : null}
                    </td>
                    <td className="px-4 py-4">
                      <RolePill r={u.role} />
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-block w-2 h-2 rounded-full ${
                          u.is_active ? "bg-[#3a5a40]" : "bg-[#9e2a2b]"
                        }`}
                      />
                      <span className="ml-2 text-xs text-[#6b655b]">
                        {u.is_active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      {u.resume_url ? (
                        <a
                          href={u.resume_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`staff-resume-link-${u.id}`}
                          className="text-[10px] uppercase text-[#234333] border border-[#234333]/30 rounded-sm px-2 py-0.5 hover:bg-[#efece6]"
                        >
                          PDF
                        </a>
                      ) : (
                        <span className="text-xs text-[#6b655b]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-[#6b655b] text-xs whitespace-nowrap">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(u)}
                        data-testid={`staff-edit-${u.id}`}
                        className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                      >
                        <Pencil size={14} strokeWidth={1.5} /> Edit
                      </button>
                      <button
                        onClick={() => setResetPwFor(u)}
                        data-testid={`staff-reset-${u.id}`}
                        className="ml-1 inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                      >
                        <KeyRound size={14} strokeWidth={1.5} /> Reset
                      </button>
                      <button
                        onClick={() => toggleActive(u)}
                        data-testid={`staff-toggle-${u.id}`}
                        disabled={u.username === me?.username}
                        className="ml-1 inline-flex items-center gap-1 text-[#6b655b] hover:bg-[#efece6] px-2 py-1 rounded-sm disabled:opacity-40"
                      >
                        <Power size={14} strokeWidth={1.5} />
                        {u.is_active ? "Disable" : "Enable"}
                      </button>
                      <button
                        onClick={() => setConfirmDelete(u)}
                        data-testid={`staff-delete-${u.id}`}
                        disabled={u.username === me?.username}
                        className="ml-1 inline-flex items-center gap-1 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm disabled:opacity-40"
                      >
                        <Trash2 size={14} strokeWidth={1.5} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Login Activity */}
          <div className="mb-3 flex items-center gap-2">
            <Activity size={14} strokeWidth={1.5} className="text-[#234333]" />
            <h2 className="font-serif text-2xl text-[#1c1a17]">Recent Login Activity</h2>
          </div>
          <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
            {activity.length === 0 ? (
              <div className="p-8 text-sm text-[#6b655b] text-center">
                No login activity recorded yet.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="activity-table">
                <thead>
                  <tr className="bg-[#f9f8f6] text-left">
                    {["When", "User", "IP", "Status"].map((h, i) => (
                      <th
                        key={i}
                        className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activity.slice(0, 50).map((a) => (
                    <tr key={a.id} className="border-t border-[#e5e0d8]">
                      <td className="px-4 py-3 text-[#1c1a17] font-mono text-xs">
                        {new Date(a.timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-[#6b655b]">{a.username}</td>
                      <td className="px-4 py-3 text-[#6b655b] font-mono text-[11px]">
                        {a.ip || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-[10px] uppercase tracking-wide border rounded-sm px-2 py-0.5 ${
                            a.success
                              ? "border-[#3a5a40]/30 text-[#3a5a40] bg-[#3a5a40]/5"
                              : "border-[#9e2a2b]/30 text-[#9e2a2b] bg-[#9e2a2b]/5"
                          }`}
                        >
                          {a.success ? "Success" : "Failed"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="staff-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit Staff" : "Add Staff"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <Field label="Full Name *">
              <input
                data-testid="staff-name-input"
                className={inputCls}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
            <Field
              label={
                editing ? "Username (read-only)" : "Username (or email if blank)"
              }
            >
              <input
                data-testid="staff-username-input"
                className={inputCls}
                value={form.username}
                disabled={!!editing}
                onChange={(e) => set("username", e.target.value)}
              />
            </Field>
            <Field label="Email">
              <input
                data-testid="staff-email-input"
                type="email"
                className={inputCls}
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </Field>
            {!editing && (
              <Field label="Initial Password *">
                <input
                  data-testid="staff-password-input"
                  className={inputCls + " font-mono"}
                  value={form.password}
                  onChange={(e) => set("password", e.target.value)}
                  placeholder="At least 6 characters"
                />
              </Field>
            )}
            <Field label="Role *" full>
              <div className="grid grid-cols-2 gap-3">
                {ROLES.map((r) => (
                  <label
                    key={r.key}
                    data-testid={`staff-role-${r.key}`}
                    className={`block border rounded-sm px-4 py-3 cursor-pointer ${
                      form.role === r.key
                        ? "border-[#234333] bg-[#234333]/5"
                        : "border-[#e5e0d8] hover:bg-[#f9f8f6]"
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      checked={form.role === r.key}
                      onChange={() => set("role", r.key)}
                      className="hidden"
                    />
                    <div className="text-sm font-semibold text-[#1c1a17]">{r.label}</div>
                    <div className="text-[11px] text-[#6b655b] mt-1">
                      {r.key === "institute_admin"
                        ? "Full control: departments, intake, fees, staff"
                        : "Can manage intake & fees only"}
                    </div>
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Designation" full>
              <select
                data-testid="staff-designation-select"
                className={inputCls}
                value={form.designation}
                onChange={(e) => set("designation", e.target.value)}
              >
                <option value="">— Select designation —</option>
                <optgroup label="Teaching">
                  {designations
                    .filter((d) => d.staff_type === "teaching")
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((d) => (
                      <option key={d.id} value={d.name}>
                        {d.name}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Non-teaching">
                  {designations
                    .filter((d) => d.staff_type === "non_teaching")
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((d) => (
                      <option key={d.id} value={d.name}>
                        {d.name}
                      </option>
                    ))}
                </optgroup>
              </select>
              <div className="text-[11px] text-[#6b655b] mt-2">
                Designations are managed by the Trust Admin under Masters → Staff Designation.
              </div>
            </Field>
            {isTeaching && (
              <Field label="Department *" full>
                <select
                  data-testid="staff-department-select"
                  className={inputCls}
                  value={form.department_id}
                  onChange={(e) => set("department_id", e.target.value)}
                >
                  <option value="">— Select department —</option>
                  {departments
                    .filter((d) => d.is_active !== false)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                        {d.code ? ` (${d.code})` : ""}
                      </option>
                    ))}
                </select>
                <div className="text-[11px] text-[#6b655b] mt-2">
                  Required for teaching staff. Pick from the institute's departments.
                </div>
              </Field>
            )}
            {!isTeaching && (
              <Field label="Department" full>
                <select
                  data-testid="staff-department-select"
                  className={`${inputCls} disabled:bg-[#f9f8f6] disabled:text-[#9e9a91] disabled:cursor-not-allowed`}
                  value=""
                  disabled
                >
                  <option value="">— Not applicable —</option>
                </select>
                <div className="text-[11px] text-[#6b655b] mt-2">
                  {form.designation
                    ? isPrincipal
                      ? "Department is not applicable for the Principal."
                      : "Department is not applicable for non-teaching staff."
                    : "Pick a teaching designation first to enable department selection."}
                </div>
              </Field>
            )}
            <Field label="Photo (max 1 MB · JPG/PNG)" full>
              <FileUploader
                kind="staff_photo"
                label=""
                url={form.photo_url}
                testid="staff-photo"
                onChange={({ url, public_id }) =>
                  setForm((f) => ({
                    ...f,
                    photo_url: url,
                    photo_public_id: public_id,
                  }))
                }
              />
            </Field>
            <Field label="Resume (PDF)" full>
              <FileUploader
                kind="staff_resume"
                label=""
                url={form.resume_url}
                testid="staff-resume"
                onChange={({ url, public_id }) =>
                  setForm((f) => ({
                    ...f,
                    resume_url: url,
                    resume_public_id: public_id,
                  }))
                }
              />
            </Field>
            <Field label="Active" full>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="staff-active-checkbox"
                  checked={form.is_active}
                  onChange={(e) => set("is_active", e.target.checked)}
                  className="w-4 h-4 accent-[#234333]"
                />
                <span className="text-sm text-[#1c1a17]">Account is enabled</span>
              </label>
            </Field>
          </div>
          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="staff-cancel"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="staff-save"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog
        open={!!resetPwFor}
        onOpenChange={(v) => !v && (setResetPwFor(null), setNewPassword(""))}
      >
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-md"
          data-testid="staff-reset-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Reset Password
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#6b655b] mb-4">
            Set a new password for{" "}
            <span className="font-mono text-[#1c1a17]">
              {resetPwFor?.username || resetPwFor?.email}
            </span>
            .
          </div>
          <input
            data-testid="staff-reset-input"
            className={inputCls + " font-mono"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="At least 6 characters"
          />
          <DialogFooter className="pt-4">
            <button
              onClick={() => {
                setResetPwFor(null);
                setNewPassword("");
              }}
              data-testid="staff-reset-cancel"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={doResetPassword}
              data-testid="staff-reset-confirm"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm"
            >
              Reset Password
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Delete this staff?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              {confirmDelete?.name || confirmDelete?.username} will lose access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="staff-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
